import apiClient from './api';

export interface Subscription {
  id: string;
  tenantId: string;
  chargebeeSubscriptionId: string;
  chargebeeCustomerId: string;
  planId: string;
  planName: string;
  status: string;
  planAmount: number;
  currency: string;
  billingPeriod: string;
  billingPeriodUnit?: number;
  trialStart?: string;
  trialEnd?: string;
  currentTermStart?: string;
  currentTermEnd?: string;
  nextBillingAt?: string;
  cancelledAt?: string;
  maxUsers: number;
  maxCourses: number;
  hasCustomBranding: boolean;
  hasApiAccess: boolean;
  createdAt: string;
  updatedAt: string;
  subscriptionType: string;  // "tenant" or "user"
  userId?: string;  // Populated for user subscriptions
  activeAddonIds: string[];  // Active add-on item price IDs
  addonQuantities: Record<string, number>;  // Per-addon purchased seat counts
  addonUnitPrices: Record<string, number>;  // Per-addon per-seat price in dollars (reflects admin overrides)
}

export interface UserSubscriptionStatus {
  hasActiveSubscription: boolean;
  subscriptionSource?: 'tenant' | 'user';
  tenantSubscription?: Subscription;
  userSubscription?: Subscription;
  canPurchaseIndividual: boolean;
}

export interface Invoice {
  id: string;
  tenantId: string;
  subscriptionId: string;
  chargebeeInvoiceId: string;
  chargebeeCustomerId: string;
  status: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  currency: string;
  date?: string;
  dueDate?: string;
  paidAt?: string;
  invoicePdfUrl?: string;
  hostedInvoiceUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface HostedPage {
  url: string;
  id: string;
  type: string;
  expiresAt: string;
}

export interface BillingPortalSession {
  portalUrl: string;
  expiresAt: string;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  period: string;
  periodUnit: number;
  status: string;
  planType: string;  // "tenant" (enterprise) or "user" (individual)
  chargebeeItemType: string;  // "plan" (base plan) or "addon"
  isMetered: boolean;  // True for seat-based plans
  maxUsers?: number;  // -1 for unlimited
  maxCourses?: number;
  hasCustomBranding: boolean;
  hasApiAccess: boolean;
}

export interface CreateSubscriptionDto {
  planId: string;
  addonIds?: string[];  // Selected add-on item price IDs
  quantity?: number;  // Base plan seat count / fallback quantity
  addonQuantities?: Record<string, number>;  // Per-addon seat counts
  couponCode?: string;
  redirectUrl?: string;  // URL to redirect to after successful checkout
}

export interface UpdateSubscriptionDto {
  planId?: string;
  quantity?: number;  // Update number of seats
}

export interface SubscriptionLimitCheck {
  isWithinLimit: boolean;
  currentUserCount: number;
  maxAllowedUsers: number;
  message?: string;
}

export interface RemainingSeats {
  tenantId: string;
  remainingSeats: number;
  isUnlimited: boolean;
}

export interface CancelSubscriptionDto {
  endOfTerm?: boolean;
  cancellationReason?: string;
}

export interface AddonAccessCheck {
  hasAccess: boolean;
  requiredAddonId?: string;
  hasPendingRequest: boolean;
  isTenantGated?: boolean;
  accessDeniedReason?: string;
}

export interface ManageUserSubscriptionDto {
  /** Full desired add-on list. Pass empty array to remove all add-ons. */
  addonIds: string[];
  redirectUrl?: string;
}

export interface ManageTenantSubscriptionDto {
  /** Full desired add-on list. Pass empty array to remove all add-ons. */
  addonIds: string[];
  /** Per-addon seat counts. If omitted for an addon, defaults to 1. */
  addonQuantities?: Record<string, number>;
  redirectUrl?: string;
}

export interface AdminTenantSubscription {
  tenantId: string;
  tenantName: string;
  isActive: boolean;
  userCount: number;
  subscription: Subscription | null;
}

export interface AdminProvisionSubscriptionDto {
  planId: string;
  quantity?: number;
  /** Selected add-on item price IDs. */
  addonIds?: string[];
  /** Per-addon seat counts. Falls back to quantity when omitted for an addon. */
  addonQuantities?: Record<string, number>;
  /** Per-addon per-seat price override in dollars. Falls back to the catalog price when omitted for an addon. */
  addonUnitPrices?: Record<string, number>;
  /** Trial length in days from now. Omit or 0 for no trial. */
  trialDays?: number;
  /** Exact trial end (ISO UTC). Takes precedence over trialDays. */
  trialEndUtc?: string;
}

export interface AdminUpdateSubscriptionDto {
  planId: string;
  quantity?: number;
  /** Complete list of add-on item price IDs — omitted add-ons are removed. */
  addonIds?: string[];
  /** Per-addon seat counts. Falls back to quantity when omitted for an addon. */
  addonQuantities?: Record<string, number>;
  /** Per-addon per-seat price override in dollars. Falls back to the catalog price when omitted for an addon. */
  addonUnitPrices?: Record<string, number>;
}

export interface AdminUpdateTrialDto {
  /** New trial end (ISO UTC). Omit to end the trial immediately. */
  trialEndUtc?: string;
}

class BillingService {
  /**
   * Get subscription for a tenant
   */
  async getSubscription(tenantId: string): Promise<Subscription | null> {
    const response = await apiClient.get(`/api/tenants/${tenantId}/billing/subscription`, {
      validateStatus: (status) => status === 200 || status === 404,
    });

    if (response.status === 404) {
      return null;
    }

    return response.data;
  }

  /**
   * Create subscription checkout
   */
  async createSubscriptionCheckout(
    tenantId: string,
    dto: CreateSubscriptionDto
  ): Promise<HostedPage> {
    const response = await apiClient.post(
      `/api/tenants/${tenantId}/billing/subscription/checkout`,
      dto
    );
    return response.data;
  }

  /**
   * Update subscription
   */
  async updateSubscription(
    tenantId: string,
    dto: UpdateSubscriptionDto
  ): Promise<Subscription> {
    const response = await apiClient.put(
      `/api/tenants/${tenantId}/billing/subscription`,
      dto
    );
    return response.data;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(
    tenantId: string,
    dto: CancelSubscriptionDto
  ): Promise<Subscription> {
    const response = await apiClient.post(
      `/api/tenants/${tenantId}/billing/subscription/cancel`,
      dto
    );
    return response.data;
  }

  /**
   * Reactivate subscription
   */
  async reactivateSubscription(tenantId: string): Promise<Subscription> {
    const response = await apiClient.post(
      `/api/tenants/${tenantId}/billing/subscription/reactivate`
    );
    return response.data;
  }

  /**
   * Reactivate individual user subscription
   */
  async reactivateUserSubscription(): Promise<Subscription> {
    const response = await apiClient.post('/api/billing/me/subscription/reactivate');
    return response.data;
  }

  /**
   * Get billing portal session
   */
  async getBillingPortalSession(tenantId: string): Promise<BillingPortalSession> {
    const response = await apiClient.get(`/api/tenants/${tenantId}/billing/portal`);
    return response.data;
  }

  /**
   * Get billing portal session for the current individual user
   */
  async getUserBillingPortalSession(): Promise<BillingPortalSession> {
    const response = await apiClient.get('/api/billing/user/portal');
    return response.data;
  }

  /**
   * Get invoices for a tenant
   */
  async getInvoices(tenantId: string): Promise<Invoice[]> {
    const response = await apiClient.get(`/api/tenants/${tenantId}/billing/invoices`);
    return response.data;
  }

  /**
   * Get available plans from Chargebee (tenant plans only)
   */
  async getAvailablePlans(): Promise<Plan[]> {
    const response = await apiClient.get('/api/billing/plans');
    return response.data;
  }

  /**
   * Generate a Chargebee checkout link for a tenant (system admin only).
   * Returns a hosted-page URL that can be emailed to the tenant admin.
   */
  async createAdminCheckoutLink(tenantId: string, dto: CreateSubscriptionDto): Promise<HostedPage> {
    const response = await apiClient.post(
      `/api/admin/tenants/${tenantId}/billing/checkout-link`,
      dto
    );
    return response.data;
  }

  /**
   * List all tenants with their subscription state (system admin only)
   */
  async getAdminTenantSubscriptions(): Promise<AdminTenantSubscription[]> {
    const response = await apiClient.get('/api/admin/tenants/subscriptions');
    return response.data;
  }

  /**
   * Provision a subscription for a tenant directly via the Chargebee API (system admin only).
   * Supports an optional per-subscription trial. Trials are created with auto-collection on
   * (Chargebee cancels at trial end without a payment method); non-trial provisions are
   * invoiced (no auto-collection).
   */
  async adminProvisionSubscription(
    tenantId: string,
    dto: AdminProvisionSubscriptionDto
  ): Promise<Subscription> {
    const response = await apiClient.post(
      `/api/admin/tenants/${tenantId}/billing/subscription`,
      dto
    );
    return response.data;
  }

  /**
   * Update a tenant's subscription — plan, seats, add-ons, and negotiated per-seat prices
   * (system admin only). No proration: the current paid term is untouched; the next renewal
   * invoice bills at the new configuration.
   */
  async adminUpdateSubscription(
    tenantId: string,
    dto: AdminUpdateSubscriptionDto
  ): Promise<Subscription> {
    const response = await apiClient.put(
      `/api/admin/tenants/${tenantId}/billing/subscription`,
      dto
    );
    return response.data;
  }

  /**
   * Pull a subscription's current state from Chargebee into the local database
   * (system admin only). Use when webhooks may have been missed and a row looks stale.
   */
  async adminSyncSubscription(chargebeeSubscriptionId: string): Promise<Subscription> {
    const response = await apiClient.post(
      `/api/billing/subscriptions/${chargebeeSubscriptionId}/sync`
    );
    return response.data;
  }

  /**
   * Change or end the trial on a tenant's in-trial subscription (system admin only).
   */
  async adminUpdateTrial(tenantId: string, dto: AdminUpdateTrialDto): Promise<Subscription> {
    const response = await apiClient.put(
      `/api/admin/tenants/${tenantId}/billing/subscription/trial`,
      dto
    );
    return response.data;
  }

  /**
   * Get user subscription status (checks both tenant and user subscriptions)
   */
  async getUserSubscriptionStatus(): Promise<UserSubscriptionStatus> {
    const response = await apiClient.get('/api/billing/me/subscription');
    return response.data;
  }

  /**
   * Get available plans for individual users (base plans only)
   */
  async getUserPlans(): Promise<Plan[]> {
    const response = await apiClient.get('/api/billing/me/plans');
    return response.data;
  }

  /**
   * Get available add-ons for individual users
   */
  async getAvailableAddons(): Promise<Plan[]> {
    const response = await apiClient.get('/api/billing/me/addons');
    return response.data;
  }

  /**
   * Get available add-ons for enterprise/tenant subscriptions
   */
  async getAvailableTenantAddons(): Promise<Plan[]> {
    const response = await apiClient.get('/api/billing/tenant/addons');
    return response.data;
  }

  /**
   * Create individual user subscription checkout
   */
  async createUserSubscriptionCheckout(dto: CreateSubscriptionDto): Promise<HostedPage> {
    const response = await apiClient.post('/api/billing/me/subscription/checkout', dto);
    return response.data;
  }

  /**
   * Sync the current user's subscription live from Chargebee (call after returning from checkout)
   */
  async syncUserSubscription(): Promise<UserSubscriptionStatus> {
    const response = await apiClient.post('/api/billing/me/subscription/sync');
    return response.data;
  }

  /**
   * Create a Chargebee hosted checkout for modifying an existing user subscription (add/remove add-ons)
   */
  async createUserManageCheckout(dto: ManageUserSubscriptionDto): Promise<HostedPage> {
    const response = await apiClient.post('/api/billing/me/subscription/manage-checkout', dto);
    return response.data;
  }

  /**
   * Create a Chargebee hosted checkout for modifying an existing tenant subscription (add/remove add-ons, adjust seats)
   */
  async createTenantManageCheckout(tenantId: string, dto: ManageTenantSubscriptionDto): Promise<HostedPage> {
    const response = await apiClient.post(`/api/tenants/${tenantId}/billing/subscription/manage-checkout`, dto);
    return response.data;
  }

  /**
   * Check whether the current user has addon access for a given course type (0=OnDemand, 1=Webinar).
   * Uses the user's assigned addon seats, not the tenant's full addon list.
   */
  async checkAddonAccess(courseType: number): Promise<AddonAccessCheck> {
    const response = await apiClient.get('/api/billing/user/addon-access-check', { params: { courseType } });
    return response.data;
  }

  /**
   * Submit an access request for an add-on to the tenant admin.
   */
  async submitAccessRequest(tenantId: string, addonId: string, requestNote?: string): Promise<void> {
    await apiClient.post(`/api/tenants/${tenantId}/access-requests`, { addonId, requestNote });
  }

  /**
   * Cancel individual user subscription
   */
  async cancelUserSubscription(dto: CancelSubscriptionDto): Promise<Subscription> {
    const response = await apiClient.post('/api/billing/me/subscription/cancel', dto);
    return response.data;
  }

  /**
   * Check subscription user limits for a tenant
   */
  async checkUserLimit(tenantId: string): Promise<SubscriptionLimitCheck> {
    const response = await apiClient.get(
      `/api/tenants/${tenantId}/billing/subscription/user-limit`
    );
    return response.data;
  }

  /**
   * Get remaining user seats for a tenant
   */
  async getRemainingSeats(tenantId: string): Promise<RemainingSeats> {
    const response = await apiClient.get(
      `/api/tenants/${tenantId}/billing/subscription/remaining-seats`
    );
    return response.data;
  }
}

export default new BillingService();
